import { Tag } from 'lucide-react'
import { Link } from 'react-router-dom'

/**
 * One source of truth for how a SmartKraal tag binding (mig 136) is named and
 * coloured across the app: the cattle list row, the animal page, the
 * registration workspace and the weighings section all render from here.
 * Phase 3 adds reviewer verbs (confirm / send back); extend the maps here.
 */
export const BINDING_STATUS = {
  unbound:   { label: 'Unbound',         short: 'unbound',         cls: 'bg-earth-100 text-earth-700 border-earth-200' },
  bound:     { label: 'Awaiting review', short: 'awaiting review', cls: 'bg-amber-50 text-amber-700 border-amber-100' },
  confirmed: { label: 'Confirmed',       short: 'confirmed',       cls: 'bg-primary-50 text-primary-700 border-primary-100' },
  sent_back: { label: 'Sent back',       short: 'sent back',       cls: 'bg-red-50 text-red-700 border-red-100' },
  retired:   { label: 'Retired',         short: 'retired',         cls: 'bg-earth-100 text-earth-500 border-earth-200' },
}

export const BIND_METHOD_LABEL = {
  import: 'spreadsheet import',
  crush_entry: 'tagging-day entry',
  photo_match: 'photo match',
  proposal_accepted: 'confirmed proposal',
  new_animal: 'registration from the crush record',
}

// Plausibility checks on a binding (Phase 3; services/smartkraal_registration_checks.py).
// Rule names and the result vocabulary come from the backend; the words are here.
export const CHECK_RULE_LABEL = {
  weight_vs_profile: 'Weight vs profile',
  sex_class_consistency: 'Sex and class',
  longitudinal_step: 'Weight change between passes',
  farm_number_agrees: 'SmartKraal farm number',
  igqabi_sex_agrees: 'Igqabi sex',
}

export const CHECK_RESULT = {
  pass:         { label: 'Checks pass',               cls: 'bg-primary-50 text-primary-700 border-primary-100' },
  fail:         { label: 'Checks flag this binding',  cls: 'bg-red-50 text-red-700 border-red-100' },
  insufficient: { label: 'No weights to check yet',   cls: 'bg-earth-100 text-earth-600 border-earth-200' },
}

// A `proposed` row is the system's suggestion, never a binding.
export const isLiveBinding = (link) => !!link && ['bound', 'confirmed', 'sent_back'].includes(link.status)

// Where on the registration page a binding of this status is listed.
export const registrationTabFor = (status) =>
  status === 'sent_back' ? '/cattle/registration?tab=sent_back'
    : status === 'bound' ? '/cattle/registration?tab=review'
    : '/cattle/registration?tab=all'

export function BindingStatusChip({ status }) {
  const s = BINDING_STATUS[status] || BINDING_STATUS.unbound
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium border ${s.cls}`}>{s.label}</span>
}

/**
 * The "SK 612 · awaiting review" chip. `to` makes it a link (the animal page
 * points at the review tab); `compact` drops the status word (list rows).
 */
export default function BindingChip({ pIdtag, status, bindMethod, boundAt, to, compact = false, testId }) {
  const s = BINDING_STATUS[status] || BINDING_STATUS.bound
  const title = `SmartKraal tag ${pIdtag} · ${s.short}` +
    (bindMethod ? ` · bound by ${BIND_METHOD_LABEL[bindMethod] || bindMethod}` : '') +
    (boundAt ? ` on ${String(boundAt).slice(0, 10)}` : '')
  const body = (
    <>
      <Tag className="h-3 w-3" />
      {compact ? `SK ${pIdtag}` : `SmartKraal tag ${pIdtag} · ${s.short}`}
    </>
  )
  const cls = `inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.cls}`
  if (to) return <Link to={to} className={cls} title={title} data-testid={testId}>{body}</Link>
  return <span className={cls} title={title} data-testid={testId}>{body}</span>
}

// What kind of electronic id an animal record carries (4b-2). An animal created from
// the crush record before the stick read it wears a placeholder (SK-<farm>-<tag>) or
// the tag's UHF EPC (hex with letters); a stick-read LF EID is digits (ISO 11784: 15).
// Mirrors _rfid_form in routes/smartkraal_registration.py; only the first two may be
// swapped through "Assign EID".
export const eidForm = (rfid) => {
  const r = String(rfid || '').trim()
  if (/^SK-/i.test(r)) return 'placeholder'
  if (/^[0-9a-f]{20,40}$/i.test(r) && !/^\d+$/.test(r)) return 'epc'
  return 'eid'
}
export const EID_FORM_LABEL = { placeholder: 'placeholder id', epc: 'UHF EPC', eid: 'EID' }
